import { InjectorService } from '@core/services/core/injector.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { asyncScheduler, from, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  finalize,
  map,
  skipWhile,
  startWith,
  switchMap,
  throttleTime
} from 'rxjs/operators';

const spinnerFactory =
  (ui: UiHelperService) =>
  <T>(id: string) =>
  (source$: Observable<T>) => {
    const LOADING_DELAY_TIME = 300;
    const LOADING_MIN_SHOW_TIME = 500;

    return source$.pipe(
      map(result => ({ result, showLoader: false, skippeableValue: false, isError: false })),
      startWith({ result: {}, showLoader: true, skippeableValue: true, isError: false }),
      catchError((err) => of({ result: err, showLoader: false, skippeableValue: false, isError: true })),
      throttleTime(LOADING_DELAY_TIME, asyncScheduler, {
        leading: false,
        trailing: true
      }),
      // this hack keeps loader up at least LOADING_MIN_SHOW_TIME seconds if data arrives
      // right after loader goes up
      concatMap(x => {
        if (x.showLoader) {
          return from(ui.presentLoading(id)).pipe(delay(LOADING_MIN_SHOW_TIME), map(_ => x));
        } else {
          return of(x);
        }
      }),
      skipWhile(x => x.skippeableValue),
      switchMap(x => {
        if (x.isError) {
          return throwError(x.result as T);
        } else {
          return of(x.result as T);
        }
      }),
      finalize(() => ui.dismissLoading(id))
    );
  };

export const spinner = <T>(id: string) =>
  spinnerFactory(InjectorService.injector.get(UiHelperService))<T>(id);